import {
  DataGridBox,
  ShowProducts,
  CustomToolbar,
  ZebraDataGrid,
  ShowUsedBanks,
  ShowCompanies,
  CustomNoRowsOverlay,
} from 'components';
import {
  GridColDef,
  GridFilterItem,
  GridRenderCellParams,
  GridRowSelectionModel,
  getGridStringOperators,
  GridFilterInputSingleSelect,
} from '@mui/x-data-grid-premium';
import { MyWindow } from 'react-app-env';
import apiService from 'library/api-service';
import { useNavigate } from 'react-router-dom';
import { Project, UsedBank } from 'library/types';
import { FormattedMessage, useIntl } from 'react-intl';
import BusinessIcon from '@mui/icons-material/Business';
import { Book, CompanyToBeEdited } from 'pages/dashboard';
import { EXPORT_TYPE_ENUM, STORAGE_KEY_ENUM } from 'enums';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import projectsActions from 'store/features/projects/actions';
import gibTypesActions from 'store/features/gib-types/actions';
import { Authentication, renderDataGridLocaleText } from 'utils';
import usedBanksActions from 'store/features/used-banks/actions';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Alert, Box, IconButton, Tooltip, Typography } from '@mui/material';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import connectionTypesActions from 'store/features/connection-types/actions';
import { selectProjects, selectProjectsIsEmpty } from 'store/features/projects/selector';
import { selectGibTypes, selectGibTypesIsEmpty } from 'store/features/gib-types/selector';
import { selectUsedBanks, selectUsedBanksIsEmpty } from 'store/features/used-banks/selector';
import { selectConnectionTypes, selectConnectionTypesIsEmpty } from 'store/features/connection-types/selector';

declare const window: MyWindow;

const getGridMultiSelectOperatorsForUsedBanks = [
  {
    value: 'contains',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (filterItem.value == null || filterItem.value === '') {
        return null;
      }

      return (value: number[]): boolean => {
        const valueArray = Array.isArray(value) ? value : [value];
        return valueArray.includes(filterItem.value);
      };
    },
    InputComponent: GridFilterInputSingleSelect,
  },
  {
    value: 'doesNotContain',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (filterItem.value == null || filterItem.value === '') {
        return null;
      }

      return (value: number[]): boolean => {
        const valueArray = Array.isArray(value) ? value : [value];
        return !valueArray.includes(filterItem.value);
      };
    },
    InputComponent: GridFilterInputSingleSelect,
  },
  // {
  //   value: 'isAnyOf',
  //   getApplyFilterFn: (filterItem: GridFilterItem) => {
  //     if (!Array.isArray(filterItem.value) || filterItem.value.length === 0) {
  //       return null;
  //     }

  //     const filterItemValues = filterItem.value;
  //     return (value: number[]): boolean => {
  //       const valueArray = Array.isArray(value) ? value : [value];

  //       return valueArray.reduce<boolean>((anyFound, val) => anyFound || filterItemValues.includes(val), false);
  //     };
  //   },
  //   InputComponent: GridFilterInputMultipleSingleSelect,
  // },
];

const getGridMultiSelectOperatorsForProducts = [
  {
    value: 'contains',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (filterItem.value == null || filterItem.value === '') {
        return null;
      }

      return (value: Project[]): boolean => {
        const reducedValue = value.map((project) => project.id);
        const valueArray = Array.isArray(reducedValue) ? reducedValue : [reducedValue];
        return valueArray.includes(filterItem.value);
      };
    },
    InputComponent: GridFilterInputSingleSelect,
  },
  {
    value: 'doesNotContain',
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (filterItem.value == null || filterItem.value === '') {
        return null;
      }

      return (value: Project[]): boolean => {
        const reducedValue = value.map((project) => project.id);
        const valueArray = Array.isArray(reducedValue) ? reducedValue : [reducedValue];
        return !valueArray.includes(filterItem.value);
      };
    },
    InputComponent: GridFilterInputSingleSelect,
  },
];

const Report = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();

  // redux hooks
  const dispatch = useAppDispatch();

  const permissionCodes = useAppSelector(selectPermissionCodes);

  const products = useAppSelector(selectProjects);
  const isEmptyProducts = useAppSelector(selectProjectsIsEmpty);

  const connectionTypes = useAppSelector(selectConnectionTypes);
  const isEmptyConnectionTypes = useAppSelector(selectConnectionTypesIsEmpty);

  const gibTypes = useAppSelector(selectGibTypes);
  const isEmptyGibTypes = useAppSelector(selectGibTypesIsEmpty);

  const usedBanks = useAppSelector(selectUsedBanks);
  const isEmptyUsedBanks = useAppSelector(selectUsedBanksIsEmpty);

  // useState hooks
  const [data, setData] = useState<Book[]>([]);
  const [loading, setLoading] = useState(false);

  const [openShowCompanies, setOpenShowCompanies] = useState(false);
  const [clickedCompanies, setClickedCompanies] = useState<CompanyToBeEdited[]>([]);

  const [openShowProducts, setOpenShowProducts] = useState(false);
  const [clickedProducts, setClickedProducts] = useState<Project[]>([]);

  const [openShowUsedBanks, setOpenShowUsedBanks] = useState(false);
  const [clickedUsedBankIds, setClickedUsedBankIds] = useState<number[]>([]);

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const incompleteAdaptation = isEmptyProducts || isEmptyConnectionTypes || isEmptyGibTypes || isEmptyUsedBanks;

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'companyName',
        headerName: intl.formatMessage({ id: 'report.companyName', defaultMessage: 'Proje Adı' }),
        flex: 0.5,
      },
      {
        field: 'companies',
        headerName: intl.formatMessage({ id: 'report.companies', defaultMessage: 'Şirketler' }),
        flex: 0.4,
        headerAlign: 'center',
        valueGetter: (value: CompanyToBeEdited[]) => {
          const reducedValue = value.reduce((acc, arrEl) => acc + ' ' + arrEl.title, '');
          return reducedValue;
        },
        renderCell: (params: GridRenderCellParams) =>
          params.row.companies.length ? (
            <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" height="100%">
              <Tooltip title={<FormattedMessage id="report.showCompanies" defaultMessage="Şirketleri Göster" />}>
                <IconButton
                  sx={{ border: 'none' }}
                  onClick={() => {
                    setClickedCompanies(params.row.companies);
                    handleClickOpenShowCompanies();
                  }}>
                  <BusinessIcon />
                </IconButton>
              </Tooltip>
              <Typography marginLeft={params.row.companies.length < 10 ? '8px' : '0px'}>{`${
                params.row.companies.length
              } ${intl.locale === 'tr' ? 'Şirket' : 'Company'}`}</Typography>
            </Box>
          ) : null,
        filterOperators: getGridStringOperators().filter(
          (operator) => operator.value === 'contains' || operator.value === 'doesNotContain'
        ),
      },
      {
        field: 'projects',
        headerName: intl.formatMessage({ id: 'report.projects', defaultMessage: 'Ürünler' }),
        flex: 0.4,
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams) => {
          const value = params.value.map((product: Project) => product.id);

          return value.length ? (
            <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" height="100%">
              <Tooltip title={<FormattedMessage id="report.showProducts" defaultMessage="Ürünleri Göster" />}>
                <IconButton
                  sx={{ border: 'none' }}
                  onClick={() => {
                    setClickedProducts(products.filter((product) => value.includes(product.id)));
                    handleClickOpenShowProducts();
                  }}>
                  <AccountTreeIcon />
                </IconButton>
              </Tooltip>
              <Typography marginLeft={params.value.length < 10 ? '8px' : '0px'}>{`${value.length} ${
                intl.locale === 'tr' ? 'Ürün' : 'Product'
              }`}</Typography>
            </Box>
          ) : null;
        },
        filterOperators: getGridMultiSelectOperatorsForProducts,
        type: 'singleSelect',
        valueOptions: () => products,
        getOptionValue: (value: Project | null) => value?.id || null,
        getOptionLabel: (value: Project | null) => value?.title || 'Hepsi',
      },
      {
        field: 'usedBankIds',
        headerName: intl.formatMessage({ id: 'report.usedBankIds', defaultMessage: 'Kullanılan Bankalar' }),
        flex: 0.4,
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams) =>
          params.value.length ? (
            <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" height="100%">
              <Tooltip
                title={<FormattedMessage id="report.showUsedBanks" defaultMessage="Kullanılan Bankaları Göster" />}>
                <IconButton
                  sx={{ border: 'none' }}
                  onClick={() => {
                    setClickedUsedBankIds(params.value);
                    handleClickOpenShowUsedBanks();
                  }}>
                  <AccountBalanceIcon />
                </IconButton>
              </Tooltip>
              <Typography marginLeft={params.value.length < 10 ? '8px' : '0px'}>{`${params.value.length} ${
                intl.locale === 'tr' ? 'Banka' : 'Bank'
              }`}</Typography>
            </Box>
          ) : null,
        filterOperators: getGridMultiSelectOperatorsForUsedBanks,
        type: 'singleSelect',
        valueOptions: () => usedBanks,
        getOptionValue: (value: UsedBank | null) => value?.id || null,
        getOptionLabel: (value: UsedBank | null) => value?.title || 'Hepsi',
      },
      {
        field: 'connectTypeId',
        headerName: intl.formatMessage({ id: 'report.connectTypeId', defaultMessage: 'Bağlantı Türü' }),
        flex: 0.25,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value: number | null) => {
          const connectType = connectionTypes.find((connectionType) => value === connectionType.id);

          return connectType?.title ?? '';
        },
        type: 'singleSelect',
        valueOptions: () => connectionTypes.map((connectionType) => connectionType.title),
      },
      {
        field: 'gibTypeId',
        headerName: intl.formatMessage({ id: 'report.gibTypeId', defaultMessage: 'GİB Yöntemi' }),
        flex: 0.4,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value: number | null) => {
          const gibType = gibTypes.find((gibType) => value === gibType.id);

          return gibType?.title ?? '';
        },
        type: 'singleSelect',
        valueOptions: () => gibTypes.map((gibType) => gibType.title),
      },
    ],
    [intl, products, connectionTypes, gibTypes, usedBanks]
  );

  // async helper functions
  const getValues = async () => {
    try {
      setLoading(true);

      const response = await apiService.getBooks();

      if (response.isSuccess) {
        setData(response.data);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const onClickDownloadHandler = async (exportType: number) => {
    const token = Authentication.getToken();
    const restUrl = process.env.REACT_APP_REST_URL || window.config.REACT_APP_REST_URL;

    const response = await fetch(`${restUrl}/Report/Books/Export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        exportType,
      }),
    });

    const arrayBuffer = await response.arrayBuffer();

    let mimeType = '';
    let fileName = '';

    if (exportType === EXPORT_TYPE_ENUM.EXCEL) {
      mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      fileName = 'export.xlsx';
    } else if (exportType === EXPORT_TYPE_ENUM.CSV) {
      mimeType = 'text/csv;charset=utf-8;';
      fileName = 'export.csv';
    } else {
      mimeType = 'application/octet-stream';
      fileName = 'export.bin';
    }

    const blob = new Blob([arrayBuffer], { type: mimeType });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();

    URL.revokeObjectURL(url);
  };

  // sync helper functions
  const handleCloseShowCompanies = () => setOpenShowCompanies(false);
  const handleClickOpenShowCompanies = () => setOpenShowCompanies(true);

  const handleCloseShowProducts = () => setOpenShowProducts(false);
  const handleClickOpenShowProducts = () => setOpenShowProducts(true);

  const handleCloseShowUsedBanks = () => setOpenShowUsedBanks(false);
  const handleClickOpenShowUsedBanks = () => setOpenShowUsedBanks(true);

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('report-page-view')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    if (!products.length && !isEmptyProducts) {
      dispatch(projectsActions.getProjects());
    }
  }, [products, isEmptyProducts, dispatch]);

  useEffect(() => {
    if (!connectionTypes.length && !isEmptyConnectionTypes) {
      dispatch(connectionTypesActions.getConnectionTypes());
    }
  }, [connectionTypes, isEmptyConnectionTypes, dispatch]);

  useEffect(() => {
    if (!gibTypes.length && !isEmptyGibTypes) {
      dispatch(gibTypesActions.getGibTypes());
    }
  }, [gibTypes, isEmptyGibTypes, dispatch]);

  useEffect(() => {
    if (!usedBanks.length && !isEmptyUsedBanks) {
      dispatch(usedBanksActions.getUsedBanks());
    }
  }, [usedBanks, isEmptyUsedBanks, dispatch]);

  useEffect(() => {
    getValues();
  }, []);

  return incompleteAdaptation ? (
    <Alert severity="info" icon={false}>
      <Typography>
        <FormattedMessage
          id="general.completeAdaptationsText"
          defaultMessage="Sayfayı görüntülemek için lütfen uyarlamaları tamamlayınız..! (Ürün Grupları, Ürünler, Bağlantı Türleri, GİB Yöntemleri, Kullanılan Bankalar)"
        />
      </Typography>
    </Alert>
  ) : (
    <>
      <ShowCompanies
        open={openShowCompanies}
        handleClose={handleCloseShowCompanies}
        clickedCompanies={clickedCompanies}
      />
      <ShowProducts open={openShowProducts} handleClose={handleCloseShowProducts} clickedProducts={clickedProducts} />
      <ShowUsedBanks
        open={openShowUsedBanks}
        handleClose={handleCloseShowUsedBanks}
        clickedUsedBankIds={clickedUsedBankIds}
      />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText()}
          rows={data}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () =>
              CustomToolbar({
                multiExport: true,
                exportAsExcelHandler: () => onClickDownloadHandler(EXPORT_TYPE_ENUM.EXCEL),
                exportAsCsvHandler: () => onClickDownloadHandler(EXPORT_TYPE_ENUM.CSV),
              }),
          }}
          slotProps={{
            filterPanel: {
              filterFormProps: {
                logicOperatorInputProps: {
                  variant: 'outlined',
                  size: 'small',
                },
                columnInputProps: {
                  variant: 'outlined',
                  size: 'small',
                  sx: { mt: 'auto' },
                },
                operatorInputProps: {
                  variant: 'outlined',
                  size: 'small',
                  sx: { mt: 'auto' },
                },
                valueInputProps: {
                  InputComponentProps: {
                    variant: 'outlined',
                    size: 'small',
                  },
                },
              },
            },
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default Report;
